import { get, isEmpty, isNil } from "lodash";
import { normalizeFieldKey } from "shared/components/ScreenRenderer/conditions";
import {
  CONDITION_OP_VALUES,
  DEFAULT_CONDITION_OPERATION,
  normalizeOperation,
  operationSymbol,
} from "./catalog";
import { conditionParts } from "./next-steps";
import { ICON_PATHS } from "./step-look";

const replacementOf = (value) => (isNil(value) ? "" : String(value));

export const fillTemplate = (template, values) =>
  Object.keys(values || {}).reduce((text, key) => {
    const pattern = new RegExp(`\\{\\{${key}\\}\\}`, "g");
    const replacement = replacementOf(values[key]);
    return String(text).replace(pattern, () => replacement);
  }, replacementOf(template));

export const WORKSHOP_ICONS = {
  back: "M15 18l-6-6 6-6",
  screen: ICON_PATHS.screen,
  check: ICON_PATHS.check,
  warn: ICON_PATHS.warn,
  info: ICON_PATHS.info,
  eye: "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  list: "M8 6h13 M8 12h13 M8 18h13 M3 6h.01 M3 12h.01 M3 18h.01",
  code: "M8 7l-5 5 5 5 M16 7l5 5-5 5",
  expand: "M15 3h6v6 M9 21H3v-6 M21 3l-7 7 M3 21l7-7",
  pick: "M4 4l7 17 2.5-7.5L21 11z",
  plus: ICON_PATHS.plus,
  close: ICON_PATHS.close,
  image: "M4 5h16v14H4z M4 16l5-5 4 4 3-3 4 4 M15.5 9.5h.01",
  send: "M12 19V5 M5 12l7-7 7 7",
  sliders: "M4 7h10 M18 7h2 M16 5v4 M4 17h4 M12 17h8 M10 15v4",
  upload: "M12 16V4 M7 9l5-5 5 5 M4 20h16",
  sparkle: "M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z",
  refresh: "M20 11a8 8 0 1 0-2.3 5.7 M20 5v6h-6",
  trash: ICON_PATHS.trash,
  text: "M5 6V4h14v2 M12 4v16 M9 20h6",
  action: ICON_PATHS.arrow,
  input: "M3 7h18v10H3z M7 10v4",
  pen: "M4 20h4L19 9l-4-4L4 16z",
  chat: "M4 5h16v11H9l-5 4z",
  collapse: "M4 4h16v16H4z M9 4v16 M16 10l-2 2 2 2",
  chevron: ICON_PATHS.chevronDown,
  phone: "M7 3h10v18H7z M11 18h2",
  busy: "M12 3v3 M12 18v3 M3 12h3 M18 12h3 M5.6 5.6l2.1 2.1 M16.3 16.3l2.1 2.1 M5.6 18.4l2.1-2.1 M16.3 7.7l2.1-2.1",
};

export const SLOT_ICONS = {
  text: WORKSHOP_ICONS.text,
  input: WORKSHOP_ICONS.input,
  action: WORKSHOP_ICONS.action,
  image: WORKSHOP_ICONS.image,
  list: WORKSHOP_ICONS.list,
};

export const WORKSHOP_VIEWS = {
  PREVIEW: "preview",
  FIELDS: "fields",
  CODE: "code",
};

export const PREVIEW_WIDTH = 1280;

export const PROPOSAL_KINDS = { STATE: "state", VERSION: "version" };

export const SLOT_TYPES = {
  TEXT: "text",
  INPUT: "input",
  ACTION: "action",
  IMAGE: "image",
  LIST: "list",
};

export const FILE_KIND = "file";

export const isFileSlot = (slot) =>
  get(slot, "type") === SLOT_TYPES.INPUT &&
  get(slot, "inputKind") === FILE_KIND;

export const slotKindOf = (slot) =>
  isFileSlot(slot) ? FILE_KIND : get(slot, "type");

export const BINDING_KINDS = {
  STATIC: "static_i18n",
  DATA: "data",
  ENTITY_FIELD: "entity_field",
  CATALOG_ACTION: "catalog_action",
  PROCESS_TRIGGER: "process_trigger",
  SET_FIELD: "set_field",
  UNBOUND: "unbound",
};

const JOURNEY_ACTIONS = ["back", "next"];
const EXECUTOR_CATEGORY = "field_executor";
const EXECUTOR_PREFIX = "field.";

export const registryActions = (catalog = []) => {
  const executors = (catalog || [])
    .filter(
      (action) =>
        action.category === EXECUTOR_CATEGORY &&
        String(action.id).indexOf(EXECUTOR_PREFIX) === 0,
    )
    .map((action) => ({
      name: action.id.slice(EXECUTOR_PREFIX.length),
      description: action.description || "",
    }));
  const describe = (name) =>
    get(
      executors.find((action) => action.name === name),
      "description",
      "",
    );
  return [
    ...JOURNEY_ACTIONS.map((name) => ({ name, description: describe(name) })),
    ...executors.filter((action) => JOURNEY_ACTIONS.indexOf(action.name) < 0),
  ];
};

export const REPORT_LEVELS = {
  UNRESOLVED: "unresolved",
  AMBIGUOUS: "ambiguous",
};

const REPORT_KEY_PREFIX = "report:";

const SLOT_DROP_RATIO = 0.3;

export const isSlotCollapse = (proposal) => {
  const before = get(proposal, "slotsBefore");
  const after = get(proposal, "slotsAfter");
  if (isNil(before) || isNil(after) || before === 0) return false;
  return after < before * (1 - SLOT_DROP_RATIO);
};

export const fieldCountText = (proposal, labels) => {
  const before = get(proposal, "slotsBefore");
  const after = get(proposal, "slotsAfter");
  if (isNil(after)) return "";
  if (isNil(before)) return labels.FIELDS.replace("{{count}}", after);
  return labels.FIELDS_CHANGE.replace("{{before}}", before).replace(
    "{{after}}",
    after,
  );
};

const leafConditions = (node) => {
  if (isNil(node)) return [];
  if (Array.isArray(node)) return node.flatMap(leafConditions);
  if (node.all) return leafConditions(node.all);
  if (node.any) return leafConditions(node.any);
  if (!isNil(node.not)) return leafConditions(node.not);
  return [node];
};

export const shortCondition = (conditions, variables, labels) => {
  const leaves = leafConditions(conditions).filter(
    (condition) => !isEmpty(get(condition, "leftOperand.accessor")),
  );
  if (!leaves.length) return null;
  const first = leaves[0];
  const parts = conditionParts(first, variables, labels);
  const operation =
    normalizeOperation(get(first, "operation")) || DEFAULT_CONDITION_OPERATION;
  const symbol = operationSymbol(operation);
  const sentence = isNil(parts.value)
    ? `${parts.field} ${parts.operation.toLowerCase()}`
    : `${parts.field} ${symbol} ${parts.value}`;
  return { sentence, more: leaves.length - 1 };
};

export const orderedStates = (states) =>
  [...(states || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

export const stateWhen = (state, variables, { labels, conditionLabels }) => {
  if (isNil(state)) return labels.DEFAULT_WHEN;
  const condition = shortCondition(
    state.conditions,
    variables,
    conditionLabels,
  );
  if (isNil(condition)) return labels.ALWAYS;
  if (condition.more)
    return fillTemplate(labels.WHEN_MORE, {
      condition: condition.sentence,
      count: condition.more,
    });
  return fillTemplate(labels.WHEN, { condition: condition.sentence });
};

export const longCondition = (conditions, variables, labels) =>
  leafConditions(conditions)
    .filter((condition) => !isEmpty(get(condition, "leftOperand.accessor")))
    .map((condition) => {
      const parts = conditionParts(condition, variables, labels);
      const emptiness = [
        CONDITION_OP_VALUES.IS_EMPTY,
        CONDITION_OP_VALUES.NOT_EMPTY,
      ].includes(normalizeOperation(get(condition, "operation")));
      if (emptiness || isNil(parts.value))
        return `${parts.field} ${parts.operation.toLowerCase()}`;
      return parts.fromField
        ? `${parts.field} ${parts.operation.toLowerCase()} ${parts.value}`
        : `${parts.field} ${parts.operation.toLowerCase()} « ${parts.value} »`;
    })
    .join(", ");

const andLeaves = (node) => {
  if (isNil(node)) return [];
  if (Array.isArray(node)) return node.flatMap(andLeaves);
  if (node.all) return andLeaves(node.all);
  if (node.any || !isNil(node.not)) return [null];
  return [node];
};

const equalityOf = (conditions, variables) => {
  const leaves = andLeaves(conditions).filter(
    (leaf) => isNil(leaf) || !isEmpty(get(leaf, "leftOperand.accessor")),
  );
  if (leaves.length !== 1 || isNil(leaves[0])) return null;
  const [leaf] = leaves;
  const operation =
    normalizeOperation(get(leaf, "operation")) || DEFAULT_CONDITION_OPERATION;
  const value = get(leaf, "rightOperand.value");
  const accessor = normalizeFieldKey(get(leaf, "leftOperand.accessor"));
  const variable = (variables || []).find(
    (item) => normalizeFieldKey(item.accessor) === accessor,
  );
  if (
    operation !== CONDITION_OP_VALUES.IDENTITY ||
    isNil(value) ||
    value === "" ||
    isNil(variable)
  )
    return null;
  return { leaf, field: variable.field, value: String(value) };
};

export const DEFAULT_STATE_SWITCH = "state:default";

const STATE_SWITCH_PREFIX = "state:";

export const stateSwitchesOf = (
  { states, active, variables },
  { labels, conditionLabels },
) => {
  if (!isNil(active)) {
    const equality = equalityOf(active.conditions, variables);
    return [
      {
        value: DEFAULT_STATE_SWITCH,
        label: labels.BACK_TO_DEFAULT,
        name: labels.BACK_TO_DEFAULT,
        disabled: isNil(equality),
        setField: isNil(equality) ? null : { field: equality.field, value: "" },
        description: isNil(equality)
          ? ""
          : fillTemplate(labels.SWITCH_CLEARS, {
              field: conditionParts(equality.leaf, variables, conditionLabels)
                .field,
            }),
      },
    ];
  }
  return (states || []).map((state) => {
    const equality = equalityOf(state.conditions, variables);
    const name = state.name || state.id;
    return {
      value: `${STATE_SWITCH_PREFIX}${state.id}`,
      label: fillTemplate(labels.SHOW_STATE, { name }),
      name: fillTemplate(labels.STATE_NAME, { name }),
      disabled: isNil(equality),
      setField: isNil(equality)
        ? null
        : { field: equality.field, value: equality.value },
      description: isNil(equality)
        ? ""
        : fillTemplate(labels.SWITCH_WRITES, {
            condition: shortCondition(
              state.conditions,
              variables,
              conditionLabels,
            ).sentence,
            name,
          }),
    };
  });
};

export const bindingKind = (slot) => {
  const kind = get(slot, "binding.kind");
  if (isNil(kind)) return BINDING_KINDS.UNBOUND;
  return kind;
};

export const isLinked = (slot) => {
  const kind = bindingKind(slot);
  if (kind === BINDING_KINDS.UNBOUND) return false;
  if (kind === BINDING_KINDS.CATALOG_ACTION) return false;
  if (kind === BINDING_KINDS.DATA) return !isEmpty(get(slot, "binding.source"));
  if (kind === BINDING_KINDS.PROCESS_TRIGGER)
    return !isEmpty(get(slot, "binding.name"));
  return true;
};

export const fieldPathOf = (slot) => {
  if (bindingKind(slot) !== BINDING_KINDS.ENTITY_FIELD) return null;
  return `${get(slot, "binding.entity")}.${get(slot, "binding.field")}`;
};

export const isPictureToProvide = (slot) =>
  get(slot, "type") === SLOT_TYPES.IMAGE && get(slot, "placeholder") === true;

export const isFieldToCreate = (slot, known) => {
  if (isNil(known) || isEmpty(known)) return false;
  const path = fieldPathOf(slot);
  if (isNil(path)) return false;
  return !Object.prototype.hasOwnProperty.call(known, path);
};

const excerpt = (value, size = 42) => {
  const text = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > size ? `${text.slice(0, size)}…` : text;
};

export const slotLabel = (slot, labels) => {
  const text = excerpt(get(slot, "label") || get(slot, "binding.sourceValue"));
  if (get(slot, "type") === SLOT_TYPES.ACTION && text)
    return labels.BUTTON.replace("{{text}}", text);
  return (
    text ||
    labels.TYPES[slotKindOf(slot)] ||
    labels.TYPES[get(slot, "type")] ||
    get(slot, "id")
  );
};

export const registryLabels = (registry = []) => {
  const labels = {};
  for (const entity of registry || []) {
    for (const field of entity.fields || []) {
      labels[`${entity.key}.${field.key}`] = field.label || null;
    }
  }
  return labels;
};

const withLabel = (path, known) =>
  known[path] ? `${path} — ${known[path]}` : path;

export const setFieldLabel = (binding, labels, known = {}) => {
  const field = withLabel(`${binding.entity}.${binding.field}`, known);
  if (isNil(binding.value) || binding.value === "") {
    return fillTemplate(labels.CLEARS_FIELD, { field });
  }
  return fillTemplate(labels.SETS_FIELD, {
    field,
    value: binding.value,
  });
};

export const slotSummary = (slot, labels, known = {}) => {
  const binding = get(slot, "binding") || {};
  if (!isLinked(slot)) return labels.UNLINKED;
  if (binding.kind === BINDING_KINDS.STATIC)
    return get(slot, "type") === SLOT_TYPES.IMAGE
      ? labels.FIXED_IMAGE
      : labels.FIXED_TEXT;
  if (binding.kind === BINDING_KINDS.DATA)
    return withLabel(binding.source, known);
  if (binding.kind === BINDING_KINDS.ENTITY_FIELD)
    return withLabel(`${binding.entity}.${binding.field}`, known);
  if (binding.kind === BINDING_KINDS.PROCESS_TRIGGER)
    return labels.ON_CLICK_VALUE.replace(
      "{{action}}",
      get(labels, ["TRIGGERS", binding.name]) || binding.name,
    );
  if (binding.kind === BINDING_KINDS.SET_FIELD)
    return labels.ON_CLICK_VALUE.replace(
      "{{action}}",
      setFieldLabel(binding, labels, known),
    );
  return binding.kind;
};

const reportEntries = (entries, level) => {
  const seen = {};
  return (entries || []).map((entry) => {
    const field = entry.dataField;
    seen[field] = (seen[field] || 0) + 1;
    return {
      key: `${REPORT_KEY_PREFIX}${level}:${field}:${seen[field]}`,
      level,
      field,
      reason: entry.reason || entry.note || "",
    };
  });
};

export const reportProblems = (report) => [
  ...reportEntries(get(report, "unresolved"), REPORT_LEVELS.UNRESOLVED),
  ...reportEntries(get(report, "ambiguous"), REPORT_LEVELS.AMBIGUOUS),
];
